/**
 * RD Core Bootstrap
 * 
 * Lightweight entry point that initializes consent manager and user store early.
 * Uses code splitting for UI components loaded later.
 */

import { consentManager } from './consent-manager';
import { userStore } from './user-store';
import { SITE_CONFIG } from './services-config';

declare global {
  interface Window {
    RD: {
      consentManager: typeof consentManager;
      userStore: typeof userStore;
      debug: {
        grantConsent: typeof consentManager.grantConsent;
        revokeConsent: typeof consentManager.revokeConsent;
        showCMP: () => void;
        showPayOrConsent: () => Promise<void>;
        showConsentBanner: () => Promise<void>;
        showFollowupConsent: (variant?: 'bar' | 'card') => Promise<void>;
        dumpState: () => { consent: any; user: any };
      };
    };
  }
}

// Reference to consent modal opener after lazy load
let openConsentModal: (() => void) | null = null;

function shouldShowPayOrConsent(): boolean {
  const user = userStore.getState();
  const consent = consentManager.getState();
  
  // Don't show if user has ad-free experience (from capabilities)
  if (user.capabilities.hasAdFreeExperience) return false;
  
  // Don't show if marketing consent already granted
  if (consent.marketing) return false;
  
  // Don't show on excluded pages
  const path = window.location.pathname;
  const excluded = SITE_CONFIG.noPayorConsentonThesePages || [];
  if (excluded.some((p: string) => path.startsWith(p))) return false;
  
  return true;
}

async function showPayOrConsentIfNeeded(): Promise<void> {
  if (!shouldShowPayOrConsent()) return;
  
  const { showPayOrConsent, isPayOrConsentVisible } = await import('./pay-or-consent');
  
  // Don't show if already visible
  if (isPayOrConsentVisible()) return;
  
  showPayOrConsent({
    onAcceptAds: async () => {
      consentManager.grantConsent('marketing');
      // Show follow-up consent for additional categories after a delay
      const { showFollowupConsent } = await import('./followup-consent');
      showFollowupConsent({
        grantedCategories: ['necessary', 'marketing'],
        onAcceptAll: () => {
          consentManager.grantConsent(['functionality', 'analytics', 'external-media']);
        },
        onCustomize: () => openConsentModal?.(),
        onDismiss: () => {}, // Do nothing, keep current consents
        variant: 'card',
        delay: 2000,
      });
    },
    onShowSettings: () => {
      openConsentModal?.();
    },
  });
}

async function bootstrap(): Promise<void> {
  // Start user fetch immediately (runs in parallel with consent manager init)
  const userFetchPromise = userStore.fetch();

  // Initialize consent manager (loads from localStorage)
  await consentManager.init();

  // Expose on window
  window.RD = {
    consentManager,
    userStore,
    debug: {
      grantConsent: consentManager.grantConsent.bind(consentManager),
      revokeConsent: consentManager.revokeConsent.bind(consentManager),
      showCMP: () => openConsentModal?.(),
      showPayOrConsent: async () => {
        const { showPayOrConsent } = await import('./pay-or-consent');
        showPayOrConsent({
          onAcceptAds: () => consentManager.grantConsent('marketing'),
          onShowSettings: () => openConsentModal?.(),
        });
      },
      showConsentBanner: async () => {
        const { showConsentBanner } = await import('./consent-banner');
        showConsentBanner({
          onAcceptAll: () => consentManager.grantConsent(['necessary', 'functionality', 'analytics', 'external-media', 'marketing']),
          onRejectAll: () => {
            // Grant necessary (required) and revoke all others
            consentManager.grantConsent(['necessary']);
            consentManager.revokeConsent(['functionality', 'analytics', 'external-media', 'marketing']);
          },
          onCustomize: () => openConsentModal?.(),
        });
      },
      showFollowupConsent: async (variant: 'bar' | 'card' = 'card') => {
        const { showFollowupConsent } = await import('./followup-consent');
        showFollowupConsent({
          grantedCategories: ['necessary', 'marketing'],
          onAcceptAll: () => consentManager.grantConsent(['functionality', 'analytics', 'external-media']),
          onCustomize: () => openConsentModal?.(),
          onDismiss: () => {},
          variant,
          delay: 500, // Shorter delay for testing
        });
      },
      dumpState: () => ({
        consent: consentManager.getState(),
        user: userStore.getState(),
      }),
    },
  };

  // Wait for user data before making decisions
  await userFetchPromise;

  // Emit ready event for plugins
  document.dispatchEvent(new CustomEvent('rd:ready', {
    detail: {
      consent: consentManager.getState(),
      user: userStore.getState(),
    },
  }));

  // Lazy-load consent modal and set up opener
  const { showConsentModal } = await import('./consent-modal');
  openConsentModal = () => {
    showConsentModal({
      getState: () => consentManager.getState(),
      grantConsent: (cats) => consentManager.grantConsent(cats),
      revokeConsent: (cats) => consentManager.revokeConsent(cats),
      onClose: () => {
        // Re-check if pay-or-consent should show after modal closes
        // (e.g., user clicked "Alle ablehnen" without changing anything)
        showPayOrConsentIfNeeded();
      },
    });
  };

  // Register the handler with consentManager for public API access
  consentManager.setShowCMPHandler(openConsentModal);

  // Attach click handlers to consent management buttons (both classes)
  document.querySelectorAll('.rd-consent-management, .rd-consent-modal-button').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      openConsentModal?.();
    });
  });

  // Show pay-or-consent if needed on initial load
  // Note: We don't subscribe to consent changes because:
  // - Grants don't need to re-show pay-or-consent
  // - Revokes trigger page reload (handled in consent-manager.ts)
  await showPayOrConsentIfNeeded();
}

// Auto-init when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}

export { consentManager, userStore };
